import { useMutation } from '@apollo/client';
import { navigate } from 'gatsby';
import React, { FC, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import spaced from '@ui/styled/Spaced';

import useMemberInsuranceInfo from '@hooks/useMemberInsurance';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';

import DELETE_MEMBER_INSURANCE from '@graphql/mutations/deleteInsurance.mutation.gql';

export interface IInsuranceRemoveConfirmationStepPageContent {
	title: string;
	removalDescription: string;
}

interface IInsuranceRemoveConfirmationStepProps extends IPageProps {
	pageContent: IInsuranceRemoveConfirmationStepPageContent;
	memberInsuranceInfoId: string;
}

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);

const StyledButtons = styled(SpacedColumn)`
	flex-flow: row nowrap;
`;

const StyledButton = styled(Button)`
	flex-grow: 1;
	width: unset;
`;

const InsuranceRemoveConfirmationStep: FC<IInsuranceRemoveConfirmationStepProps> = ({
	pageContent,
	memberInsuranceInfoId,
}) => {
	const { title, removalDescription } = pageContent;

	const [removeSubmitting, setRemoveSubmitting] = useState(false);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const [deleteMemberInsurance] = useMutation<
		{ clientMutationId: string },
		{ clientMutationId: string; id: string }
	>(DELETE_MEMBER_INSURANCE, { client: graphQLv2Client });

	const { refetchMemberInsurance } = useMemberInsuranceInfo();

	const handleRemove = useCallback(() => {
		setRemoveSubmitting(true);
		deleteMemberInsurance({
			variables: {
				clientMutationId: memberInsuranceInfoId,
				id: memberInsuranceInfoId,
			},
		})
			.then((response) => {
				console.log(`insurance ${response.data?.clientMutationId} removed`);
				refetchMemberInsurance?.();
				navigate('../../coverage');
			})
			.catch((err) => {
				console.error(`Error removing insurance ${memberInsuranceInfoId}`);
				console.error(err);
			})
			.finally(() => {
				setRemoveSubmitting(false);
			});
	}, [deleteMemberInsurance, memberInsuranceInfoId, refetchMemberInsurance]);

	return (
		<CenteredGridItem>
			<SpacedColumn gap="lgxl" marginBottom="xl" marginTop="md">
				<HeadingText>{title}</HeadingText>
				<SpacedColumn gap="sm">
					<BasicText>{removalDescription}</BasicText>
				</SpacedColumn>
				<StyledButtons gap="sm">
					<StyledButton
						label="No"
						onClick={() => navigate(`../insurance_details_edit/`)}
						labelSize="sm"
					/>
					<StyledButton
						label="Yes"
						onClick={handleRemove}
						submitting={removeSubmitting}
						variant="secondary"
						labelSize="sm"
						as={SubmitButton}
					/>
				</StyledButtons>
			</SpacedColumn>
		</CenteredGridItem>
	);
};

export default InsuranceRemoveConfirmationStep;
